import {
  Activity,
  ArrowLeftRight,
  BookMarked,
  Code,
  FileText,
  GitBranch,
  GitMerge,
  Globe,
  LayoutDashboard,
  Layers,
  Puzzle,
  Rocket,
  ScrollText,
  Server,
  Shield,
  TestTube,
  Zap,
  Cpu,
  type LucideIcon,
} from 'lucide-react'

export interface NavigationChild {
  id: string
  label: string
  migrated: boolean
  to?: string
}

export interface NavigationGroup {
  id: string
  label: string
  labelSuffix?: string
  icon: LucideIcon
  disabled?: boolean
  badge?: string
  items: NavigationChild[]
}

export interface NavigationItem {
  id: string
  label: string
  icon: LucideIcon
  to?: string
  migrated: boolean
  groups?: NavigationGroup[]
}

export interface NavigationSection {
  label?: string
  items: NavigationItem[]
}

export function isNavigationPathActive(pathname: string, to?: string) {
  if (!to) return false
  return pathname === to || pathname.startsWith(`${to}/`)
}

export const navigationSections: NavigationSection[] = [
  {
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        to: '/dashboard',
        migrated: true,
      },
      {
        id: 'onboarding',
        label: 'Onboarding',
        icon: FileText,
        to: '/onboard',
        migrated: true,
      },
    ],
  },
  {
    label: 'Lifecycle',
    items: [
      {
        id: 'microservice',
        label: 'Microservice',
        icon: Server,
        to: '/resources/microservice',
        migrated: true,
      },
      {
        id: 'proxy',
        label: 'Proxy',
        icon: Layers,
        migrated: true,
        groups: [
          {
            id: 'apigee-x',
            label: 'Apigee X',
            labelSuffix: 'Gateway',
            icon: Globe,
            items: [
              {
                id: 'apigee-proxy',
                label: 'Proxy',
                to: '/resources/apigee-proxy',
                migrated: true,
              },
              {
                id: 'apigee-shared-flow',
                label: 'Shared Flow',
                to: '/resources/apigee-shared-flow',
                migrated: true,
              },
              {
                id: 'apigee-config',
                label: 'Config',
                to: '/resources/apigee-config',
                migrated: true,
              },
            ],
          },
          {
            id: 'kong-konnect',
            label: 'Kong Konnect',
            labelSuffix: 'Gateway',
            icon: Cpu,
            items: [
              {
                id: 'kong-gateway-service',
                label: 'Gateway Services',
                to: '/resources/kong-gateway-service',
                migrated: true,
              },
              {
                id: 'kong-gateway-builder',
                label: 'Gateway Builder',
                to: '/resources/kong-gateway-builder',
                migrated: true,
              },
            ],
          },
          {
            id: 'forgesphere-gateway',
            label: 'ForgeSphere',
            labelSuffix: 'Gateway',
            icon: Server,
            items: [
              {
                id: 'forgesphere-proxy',
                label: 'API',
                to: '/resources/forgesphere-proxy',
                migrated: true,
              },
              {
                id: 'forgesphere-function',
                label: 'Function',
                to: '/resources/forgesphere-function',
                migrated: true,
              },
              {
                id: 'forgesphere-config',
                label: 'Config',
                to: '/resources/forgesphere-config',
                migrated: true,
              },
            ],
          },
          {
            id: 'mulesoft',
            label: 'MuleSoft',
            labelSuffix: 'Gateway',
            icon: Layers,
            disabled: true,
            badge: 'soon',
            items: [
              {
                id: 'mulesoft-proxy',
                label: 'Proxy',
                migrated: false,
              },
              {
                id: 'mulesoft-shared-flow',
                label: 'Shared Flow',
                migrated: false,
              },
            ],
          },
        ],
      },
      {
        id: 'mcp',
        label: 'MCP',
        icon: Puzzle,
        to: '/resources/mcp',
        migrated: true,
      },
      {
        id: 'migration',
        label: 'Migration',
        icon: ArrowLeftRight,
        to: '/migration',
        migrated: true,
      },
    ],
  },
  {
    label: 'Management',
    items: [
      {
        id: 'workflow',
        label: 'Workflow',
        icon: GitBranch,
        to: '/management/workflows',
        migrated: true,
      },
      {
        id: 'audit',
        label: 'Audit Logs',
        icon: ScrollText,
        to: '/management/audit-logs',
        migrated: true,
      },
      {
        id: 'cicd',
        label: 'CI/CD',
        icon: Zap,
        to: '/management/cicd',
        migrated: true,
      },
      {
        id: 'approvals',
        label: 'Approvals',
        icon: GitMerge,
        to: '/management/approvals',
        migrated: true,
      },
    ],
  },
  {
    label: 'Publish & Test',
    items: [
      {
        id: 'deploy',
        label: 'API Deploy',
        icon: Rocket,
        to: '/publish/deploy',
        migrated: true,
      },
      {
        id: 'test',
        label: 'Test',
        icon: TestTube,
        to: '/publish/test',
        migrated: true,
      },
      {
        id: 'governance',
        label: 'Governance',
        icon: Shield,
        to: '/publish/governance',
        migrated: true,
      },
      {
        id: 'monitoring',
        label: 'Monitoring',
        icon: Activity,
        to: '/publish/monitoring',
        migrated: true,
      },
    ],
  },
  {
    label: 'Libraries',
    items: [
      {
        id: 'spec-library',
        label: 'Spec Library',
        icon: BookMarked,
        to: '/spec-library',
        migrated: true,
      },
      { id: 'ide', label: 'IDE', icon: Code, migrated: false },
    ],
  },
]
